import { useCallback, useEffect, useMemo, useState } from 'react';
import { siteApi } from '../tjenester/siteApi';
import { useAdminPropertyForm } from './useAdminPropertyForm';
import { useAdminSettingsForm } from './useAdminSettingsForm';

const adminLoadingTimeoutMs = 15000;

export function useAdminPage() {
  const [session, setSession] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loginForm, setLoginForm] = useState({ username: 'admin', password: 'OmsorgBygg123' });
  const [loginError, setLoginError] = useState('');
  const [dashboardError, setDashboardError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [saveMessage, setSaveMessage] = useState('');
  const {
    editingSlug,
    beginEditingProperty,
    clearDeletedProperty,
    imageUploadError,
    isUploadingImage,
    propertyForm,
    removePropertyImage,
    resetPropertyForm,
    setPropertyForm,
    uploadPropertyImage,
  } = useAdminPropertyForm();
  const { applySettings, settingsForm, setSettingsForm } = useAdminSettingsForm();

  const refreshDashboard = useCallback(async () => {
    const payload = await siteApi.getAdminDashboard();
    setDashboard(payload);
    applySettings(payload.settings);
    setDashboardError('');
  }, [applySettings]);

  useEffect(() => {
    let isMounted = true;
    const loadingTimeout = setTimeout(() => {
      if (!isMounted) {
        return;
      }

      setDashboardError('Admin lastet ikke innen forventet tid. Prov a oppdatere siden eller logg inn pa nytt.');
      setIsLoading(false);
    }, adminLoadingTimeoutMs);

    siteApi
      .getAdminSession()
      .then(async (payload) => {
        if (!isMounted) {
          return;
        }

        setSession(payload.admin);
        if (payload.admin) {
          await refreshDashboard();
        }
      })
      .catch((error) => {
        if (isMounted) {
          setDashboardError(error.message);
        }
      })
      .finally(() => {
        if (isMounted) {
          clearTimeout(loadingTimeout);
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
      clearTimeout(loadingTimeout);
    };
  }, [refreshDashboard]);

  const sortedProperties = useMemo(() => dashboard?.properties || [], [dashboard]);
  const inquiries = useMemo(() => dashboard?.inquiries || [], [dashboard]);

  const handleLoginSubmit = async (event) => {
    event.preventDefault();

    try {
      const payload = await siteApi.login(loginForm);
      setSession(payload.admin);
      setLoginError('');
      await refreshDashboard();
    } catch (error) {
      setLoginError(error.message);
    }
  };

  const handleLogout = async () => {
    await siteApi.logout();
    setSession(null);
    setDashboard(null);
    setSaveMessage('');
  };

  const handlePropertySubmit = async (event) => {
    event.preventDefault();

    if (!/^\d{4}$/.test(String(propertyForm.areaCode || '').trim())) {
      setDashboardError('Omradekode ma vaere noyaktig 4 siffer.');
      return;
    }

    try {
      await siteApi.saveProperty(propertyForm, editingSlug);
      resetPropertyForm();
      setSaveMessage('Eiendommen ble lagret.');
      setDashboardError('');
      await refreshDashboard();
    } catch (error) {
      setDashboardError(error.message);
    }
  };

  const handleEditProperty = (property) => {
    beginEditingProperty(property);
    setSaveMessage('');
  };

  const handleDeleteProperty = async (slug) => {
    if (!window.confirm('Vil du slette denne eiendommen?')) {
      return;
    }

    try {
      await siteApi.deleteProperty(slug);
      setSaveMessage('Eiendommen ble slettet.');
      clearDeletedProperty(slug);
      await refreshDashboard();
    } catch (error) {
      setDashboardError(error.message);
    }
  };

  const handleSettingsSubmit = async (event) => {
    event.preventDefault();

    try {
      const payload = await siteApi.updateSettings(settingsForm);
      applySettings(payload.settings);
      setSaveMessage('Innstillingene ble lagret.');
      setDashboardError('');
    } catch (error) {
      setDashboardError(error.message);
    }
  };

  return {
    dashboardError,
    editingSlug,
    handleDeleteProperty,
    handleEditProperty,
    handleLoginSubmit,
    handleLogout,
    handlePropertySubmit,
    handleSettingsSubmit,
    imageUploadError,
    isLoading,
    isUploadingImage,
    loginError,
    loginForm,
    inquiries,
    propertyForm,
    removePropertyImage,
    resetPropertyForm,
    saveMessage,
    session,
    setLoginForm,
    setPropertyForm,
    setSettingsForm,
    settingsForm,
    sortedProperties,
    uploadPropertyImage,
  };
}